import {
  DefaultHttpClient,
  HubConnection,
  HubConnectionBuilder,
  HubConnectionState,
  LogLevel,
} from "@microsoft/signalr";
import { flow, Instance, types } from "mobx-state-tree";
import { reaction } from "mobx";
import PubSub from "pubsub-js";

import { globalModule } from "../service-provider/GlobalModule";
import { registerGlobalService } from "../service-provider/ServiceRegistry";
import { PubSubEvents } from "../PubSubEvents";
import { NotificationEvents } from "./NotificationEvents";
import { configuration } from "../configuration/ConfigurationService";
import { authentication } from "../authentication/Authentication";
import { permissionsService } from "../authorization/UserPermissionsService";
import { LoggerModel } from "../loggers/LoggerModel";
import { networkStatus } from "../network-status/NetworkStatus";
import { organizationSubscriptionService } from "../organization/OrganizationSubscription";

export interface Notification {
  id: number;
  event: string;
  message: string;
  data: any;
  organizationId: number;
  userId: number;
}

export const NotificationsServiceModel = types
  .model({
    signalRConnectionNeedsToBeUpdated: types.optional(types.boolean, false),
    logger: types.optional(LoggerModel, {
      enabled: true,
    }),
  })
  .volatile<{ signalRConnection: HubConnection | null }>((self) => ({
    signalRConnection: null,
  }))
  .actions((self) => ({
    setSignalRNeedsToBeUpdated: (needsToBeUpdated: boolean) => {
      self.signalRConnectionNeedsToBeUpdated = needsToBeUpdated;
    },
    start: flow(function* _startConnection() {
      if (self.signalRConnection) {
        try {
          yield self.signalRConnection.start();
          console.log("Connected to signalR!");
        } catch (error) {
          if (error) {
            console.error(error);
          }
        }
      }
    }),
    stop: flow(function* _stopConnection() {
      if (self.signalRConnection) {
        try {
          yield self.signalRConnection?.stop();
          console.log("Disconnected from SignalR");
          console.log("Connection after disconnected", self.signalRConnection);
        } catch (err) {
          console.log("Disconnection failed: ", err);
        }
      }
    }),
    onNotificationReceived: (notification: Notification) => {
      switch (notification.event) {
        case NotificationEvents.InspectionReleased:
          PubSub.publish(PubSubEvents.InspectionReleased, notification);
          break;
        case NotificationEvents.VideoAnalysisFinished:
          PubSub.publish(PubSubEvents.VideoAnalysisFinished, notification);
          break;
        case NotificationEvents.VideoConversionFinished:
          PubSub.publish(PubSubEvents.VideoConversionFinished, notification);
          break;
        case NotificationEvents.VideoSegmentationFinished:
          PubSub.publish(PubSubEvents.VideoSegmentationFinished, notification);
          break;
        case NotificationEvents.InspectionFileUploadFinished:
          PubSub.publish(PubSubEvents.InspectionFileUploadFinished, notification);
          break;
        case NotificationEvents.OrganizationCollaborationStopped:
          PubSub.publish(PubSubEvents.OrganizationCollaborationStopped, notification);
          break;
        case NotificationEvents.FeatureFlagsChanged:
          PubSub.publish(PubSubEvents.FeatureFlagsChanged, notification);
          break;
        case NotificationEvents.VideoOcrFinished:
          PubSub.publish(PubSubEvents.VideoOcrFinished, notification);
          break;
        default:
          throw new Error("notification not handled");
      }
    },
  }))
  .actions((self) => ({
    setUpSignalRConnection: flow(function* _setUpSignalRConnection() {
      const configurationContent = yield configuration.get();
      const accessToken = yield authentication.getAccessToken();

      const connection: HubConnection = new HubConnectionBuilder()
        .configureLogging(LogLevel.Debug)
        .withUrl(`${configurationContent.api}/hubs/notificationsPublisher`, {
          accessTokenFactory: () => accessToken,
          httpClient: new HttpClient(),
        })
        .withAutomaticReconnect()
        .build();

      self.setSignalRNeedsToBeUpdated(false);

      connection.on("OnPublishMessage", (notification: Notification) => {
        self.onNotificationReceived(notification);
      });

      connection.onreconnecting(async (error: Error | undefined) => {
        console.log("onreconnecting triggered, error:", error);
        if (error) self.setSignalRNeedsToBeUpdated(true);
      });

      connection.onclose(async (error: Error | undefined) => {
        console.log("onclose triggered");
        self.start();
      });

      self.signalRConnection = connection;
      self.start();
    }),
    stopConnection: () => {
      self.stop();
    },
  }))
  .actions((self) => {
    return {
      afterCreate() {
        if (permissionsService.hasPermissions && !self.signalRConnection)
          self.setUpSignalRConnection();
      },
      stopAndRecreateConnection: () => {
        self.stop();
        self.setUpSignalRConnection();
      },
      beforeDestroy() {
        self.stop();
      },
    };
  });

export type NotificationsService = Instance<typeof NotificationsServiceModel>;
export const notificationsService = NotificationsServiceModel.create();
globalModule.notificationsService = notificationsService;
export default registerGlobalService(notificationsService as NotificationsService);

reaction(
  () => organizationSubscriptionService.isLoading,
  () => {
    if (!organizationSubscriptionService.isLoading) {
      notificationsService.setUpSignalRConnection();
    }
  }
)

reaction(
  () => permissionsService.hasPermissions,
  () => {
    if (permissionsService.hasPermissions && !notificationsService.signalRConnection)
      notificationsService.setUpSignalRConnection();
  }
);

reaction(
  () => networkStatus.online,
  () => {
    if (
      networkStatus.online &&
      (thereIsNoExistingSignalRConnection() || thereIsADisconnectedSignalRConnection())
    ) {
      notificationsService.setUpSignalRConnection();
      setTimeout(() => {
        if (thereIsNoExistingSignalRConnection() || thereIsADisconnectedSignalRConnection()) {
          notificationsService.setUpSignalRConnection();
        }
      }, 5000);
    }

    function thereIsNoExistingSignalRConnection() {
      return !notificationsService.signalRConnection;
    }

    function thereIsADisconnectedSignalRConnection() {
      return (
        notificationsService.signalRConnection &&
        (notificationsService.signalRConnection.state === HubConnectionState.Disconnected ||
          notificationsService.signalRConnection.state === HubConnectionState.Disconnecting)
      );
    }
  }
);

reaction(
  () => networkStatus.online,
  () => {
    if (
      !networkStatus.online &&
      notificationsService.signalRConnection &&
      notificationsService.signalRConnection.state === HubConnectionState.Connected
    )
      notificationsService.stopConnection();
  }
);

reaction(
  () => notificationsService.signalRConnectionNeedsToBeUpdated,
  () => {
    if (
      notificationsService.signalRConnectionNeedsToBeUpdated &&
      notificationsService.signalRConnection
    )
      notificationsService.stopAndRecreateConnection();
  }
);

class HttpClient extends DefaultHttpClient {
  constructor() {
    super(console);
  }

  public async send(request: signalR.HttpRequest): Promise<signalR.HttpResponse> {
    const organizationSubscriptionKey = organizationSubscriptionService.data;

    request.headers = {
      ...request.headers,
      "Ocp-Apim-Subscription-Key": organizationSubscriptionKey,
    };
    if (request.method === "POST") request.timeout = 10000;

    return super.send(request);
  }
}
